import { useEffect, useState } from 'react';
import type { Task } from '../lib/types';
import { codLabel, money } from '../lib/money';
import { Bell } from 'lucide-react';
import { InlineIcon } from './InlineIcon';

/**
 * 待接单卡片 —— 最高优先级。
 *
 * 橙色 + 呼吸动画 + 倒计时。这是唯一需要"抢时间"的操作,
 * 视觉上必须比其他任何东西醒目。
 */
const COLLAPSED = 3;

export function PendingAcceptCard({
  tasks,
  busy,
  mode = 'accept',
  onAcceptAll,
  onAccept,
  onDismiss,
}: {
  tasks: Task[];
  busy: boolean;
  /**
   * ★ 两种模式,同一张卡。
   *
   *   `accept` —— 派单要司机确认:有倒计时(超时会回到待派)、有「接单」按钮。
   *   `notify` —— 开了「派单免确认」:单子已经是他的了,
   *               这张卡只负责**让他知道来活了**,没有倒计时、没有接单按钮。
   *
   *   做成模式而不是再写一个几乎一样的组件:这张卡的作用
   *   ——「让新活不可能被忽略」—— 两种情形是同一个,只有动作不同。
   */
  mode?: 'accept' | 'notify';
  onAcceptAll: () => void;
  onAccept: (t: Task) => void;
  onDismiss?: () => void;
}) {
  /**
   * 默认只显示 3 条,避免这张卡把整屏占满。
   *
   * ★ 但必须能展开:原本超出的部分只有一行「还有 N 单…」,
   *   司机既看不到是哪几单,也没法单独接 —— 只剩「全部接单」一条路。
   *   要么全要、要么什么都点不了,而且是在**不知道内容**的情况下决定。
   */
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? tasks : tasks.slice(0, COLLAPSED);

  const earliest = tasks
    .map((t) => t.acceptExpiresAt)
    .filter((x): x is string => Boolean(x))
    .sort()[0];

  return (
    <div
      className="mx-4 mt-3 rounded-2xl border-2 border-amber-400 dark:border-amber-600
                 bg-amber-50 dark:bg-amber-950/40 p-4 animate-[pulse_2.5s_ease-in-out_infinite]"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-amber-900 dark:text-amber-200">
          <InlineIcon icon={Bell} /> 新任务 ({tasks.length})
        </h3>
        {/* 免确认模式下没有「超时」这回事,倒计时是纯噪音 */}
        {mode === 'accept' && earliest && <Countdown until={earliest} />}
      </div>

      {/*
        ★ 给这个列表起个名字。主任务列表现在不折叠了,同一单会在
          「新任务」和「任务列表」里各出现一次 —— 不起名字的话,
          用例(和读屏)都分不出自己在看哪一个。
      */}
      <ul aria-label="待接单列表" className="mt-2 space-y-1">
        {shown.map((t) => (
          <li key={t.id} className="flex items-start justify-between gap-2 text-sm">
            <div className="min-w-0 flex-1">
              <div className="truncate text-amber-900 dark:text-amber-200">
                {t.pickupPoint?.name ?? '取货'} → {t.receiverName}
              </div>
              {/*
                ★ 「送到哪」比「送给谁」重要得多。
                  「张三」不告诉司机任何东西;「Waiheke Island」告诉他这是四十公里山路。
                  原来这一行只有名字 —— 他要决定接不接,却看不到要跑多远。
              */}
              {/*
                ★ 地址**不截断**。上面那句注释说的正是「送到哪比送给谁重要」,
                  而 `truncate` 把它砍掉一半 —— 恰恰砍掉的是区和门牌那部分,
                  也就是「这单要跑多远」的答案。要决定接不接,就得看全。
              */}
              <div className="break-words text-xs text-amber-700/80 dark:text-amber-400/80">
                {t.receiverAddress}
              </div>
              <div className="mt-0.5 flex flex-wrap gap-x-2 text-xs">
                {/*
                  ★ 这一单给多少,必须出现在**接单之前**。
                    原来只有收入页看得到,而那是**送完之后**的事 ——
                    等他看到金额,决定早就做完了。

                    计价没启用时 payout 是 0,这时候不显示 ——
                    在一个任务旁边写「MYR 0.00」比什么都不写更伤。
                */}
                {Number(t.payout) > 0 && (
                  <span className="font-medium text-amber-900 dark:text-amber-200">
                    {money(t.payout, t.currency)}
                  </span>
                )}
                {codLabel(t.codAmount, t.codPending, t.currency) && (
                  // 代收要垫钱/收现金,是接不接的一个真实考量
                  <span className="text-amber-700 dark:text-amber-400">
                    代收 {codLabel(t.codAmount, t.codPending, t.currency)}
                  </span>
                )}
                {t.packageCount > 1 && (
                  <span className="text-amber-700/80 dark:text-amber-400/80">
                    {t.packageCount} 件
                  </span>
                )}
              </div>
            </div>
            {mode === 'accept' && (
              <button
                onClick={() => onAccept(t)}
                disabled={busy}
                className="shrink-0 px-3 py-1 rounded-lg bg-white dark:bg-amber-900
                           text-amber-900 dark:text-amber-100 text-sm disabled:opacity-50"
              >
                接单
              </button>
            )}
          </li>
        ))}
        {tasks.length > COLLAPSED && (
          <li>
            <button
              onClick={() => setExpanded((v) => !v)}
              className="w-full py-1.5 text-sm text-amber-700 dark:text-amber-400 text-left"
            >
              {expanded ? '收起' : `展开其余 ${tasks.length - COLLAPSED} 单`}
            </button>
          </li>
        )}
      </ul>

      <button
        onClick={mode === 'accept' ? onAcceptAll : onDismiss}
        disabled={busy}
        className="mt-3 w-full h-13 py-3 rounded-xl bg-amber-500 text-white text-lg font-medium
                   disabled:opacity-50 active:bg-amber-600 transition"
      >
        {mode === 'notify'
          ? '知道了'
          : busy
            ? '接单中…'
            : `全部接单 (${tasks.length})`}
      </button>
    </div>
  );
}

/** 接单倒计时。超时后订单会回到派单池 */
function Countdown({ until }: { until: string }) {
  const [left, setLeft] = useState(() => new Date(until).getTime() - Date.now());

  useEffect(() => {
    const t = setInterval(() => setLeft(new Date(until).getTime() - Date.now()), 1000);
    return () => clearInterval(t);
  }, [until]);

  if (left <= 0) {
    return <span className="text-sm font-medium text-red-600 dark:text-red-400">已超时</span>;
  }

  const mm = Math.floor(left / 60_000);
  const ss = Math.floor((left % 60_000) / 1000);
  const urgent = left < 60_000;

  return (
    <span
      className={`text-sm font-mono font-semibold tabular-nums ${
        urgent ? 'text-red-600 dark:text-red-400' : 'text-amber-700 dark:text-amber-300'
      }`}
    >
      剩 {mm}:{String(ss).padStart(2, '0')}
    </span>
  );
}
